import _ from "lodash";
import { action, computed, decorate, observable } from "mobx";
import Sort from "models/Sort";
import parsePitsNode from "utils/parsePitsNode";
import { toast } from "react-toastify";

function formatValue(value, quoted) {
  if (quoted) {
    return `"${value.trim()}"`;
  }

  return value.trim();
}

function stringifyPitsValue(clause, property) {
  let quoted = true;
  if (clause.type === "NUMBER") {
    quoted = false;
  }

  // Gather the values we need to stringify
  // Property type of Value is free text,
  // Values is selections from react select (aka predefined values)
  let valuesToStringify = [];
  if (property === "value") {
    valuesToStringify = clause.value.split(",");
  } else {
    valuesToStringify = clause.values.map(v => v.value);
  }

  // If just a single clause, trim and return
  if (valuesToStringify.length === 1) {
    return formatValue(valuesToStringify[0], quoted);
  }

  return `[${valuesToStringify.map(v => formatValue(v, quoted))}]`;
}

function queryReducer(accumulator, clause) {
  const value = !_.isEmpty(clause.value)
    ? stringifyPitsValue(clause, "value")
    : stringifyPitsValue(clause, "values");
  return `${accumulator} ${clause.fieldName} ${clause.operator} ${value} ${clause.logicalConjunctionOperator}`.trim();
}

class BasicSearchStore {
  clauses = [];
  primarySort = new Sort();
  secondarySort = new Sort();
  returning = [];

  playsOnly = true;

  constructor(pitsApi, pitsMetadataStore) {
    this.pitsApi = pitsApi;
    this.pitsMetadataStore = pitsMetadataStore;
    this.primarySort.fieldName = "Timestamp";
    this.primarySort.isEnabled = true;
  }

  prependClause(clause) {
    if (_.isEmpty(this.clauses)) {
      // This should never happen, but just in case.
      this.clauses = [clause];
    } else if (_.isEmpty(this.clauses[0].value)) {
      // If the first clause has no value, overwrite it.
      this.clauses[0] = clause;
    } else {
      // Otherwise, prepend an ANDed clause.
      clause.logicalConjunctionOperator = "AND";
      clause.hasNext = true;
      this.clauses.unshift(clause);
    }
  }

  addClause(clause) {
    if (this.clauses.length > 0) {
      this.clauses[this.clauses.length - 1].hasNext = true;
      this.clauses[this.clauses.length - 1].logicalConjunctionOperator = "AND";
    }
    this.clauses.push(clause);
  }

  parseNode(node) {
    switch (node.nodeType) {
      case "OPTION":
        if (node.name.startsWith("ORDER BY")) {
          const [primary] = node.identifiers;
          this.primarySort.fieldName = primary;
          this.primarySort.isEnabled = true;
          this.primarySort.setIsDescFromValue(
            node.name.split(" ")[3].toLowerCase()
          );
        } else if (node.name.startsWith("RETURNING")) {
          this.returning = node.identifiers
            .map(i => this.pitsMetadataStore.getFieldByName(i))
            .filter(f => f !== undefined)
            .map(f => {
              return { label: f.displayName, value: f.name };
            });
        }
        return [];

      default:
        return parsePitsNode(
          node,
          this.pitsMetadataStore.fieldsWithDistinctValues
        );
    }
  }

  setClauses(query, queryType) {
    return this.pitsApi.getQueryInfo([query], queryType).then(resp => {
      if (resp[0].valid) {
        try {
          this.returning = [];

          const rootNode = resp[0].graph;
          let clauses = _.map(rootNode.children, n => this.parseNode(n));
          clauses = _.flattenDeep(clauses);

          // IdType (Plays Only) is handled differently than other filters
          if (queryType === "STRUCTURED") {
            const idTypeClause = _.find(clauses, c => c.fieldName === "IdType");
            if (!idTypeClause) {
              this.playsOnly = false;
            }
            clauses = _.filter(clauses, c => c.fieldName !== "IdType");
          }

          clauses.forEach(c => {
            c.type = this.pitsMetadataStore.fields[c.fieldId].type;
            c.fieldHelperText = this.pitsMetadataStore.fields[
              c.fieldId
            ].helperText;
          });

          this.clauses.replace(clauses);
          this.recomputeHasNext();
        } catch (err) {
          console.log(err);
        }
      } else {
        toast.error(`${query} is an invalid query`, {
          position: "top-right",
          autoClose: 3000
        });
      }
    });
  }

  removeClause(clauseIdx) {
    this.clauses = this.clauses.filter((c, i) => clauseIdx !== i);
    this.recomputeHasNext();
  }

  recomputeHasNext() {
    _.each(this.clauses, (c, i) => {
      if (i === this.clauses.length - 1) {
        c.hasNext = false;
        c.logicalConjunctionOperator = "";
      } else {
        c.hasNext = true;
      }
    });
  }

  isValid() {
    return _.every(
      _.map(this.clauses, c =>
        c.isValid(this.pitsMetadataStore.fieldsWithDistinctValues)
      ),
      v => v === true
    );
  }

  resetClauses() {
    this.clauses = [];
  }

  get pitsQuery() {
    let mainQuery = _.reduce(this.clauses, queryReducer, "");
    if (this.playsOnly) {
      mainQuery = `${mainQuery} AND IdType = "PLAY"`;
    }

    const orderByValues = _.map(
      _.filter([this.primarySort, this.secondarySort], s => s.isEnabled),
      s => s.pitsQuery
    ).join(", ");
    if (this.primarySort.isEnabled || this.secondarySort.isEnabled) {
      mainQuery = `${mainQuery} Order By ${orderByValues}`;
    }

    if (this.returning.length > 0) {
      const returningValues = this.returning.map(r => r.value).join(",");
      mainQuery = `${mainQuery} Returning ${returningValues}`;
    }

    return mainQuery;
  }
}

decorate(BasicSearchStore, {
  clauses: observable,
  primarySort: observable,
  secondarySort: observable,
  returning: observable,
  playsOnly: observable,
  addClause: action,
  recomputeHasNext: action,
  removeClause: action,
  resetClauses: action,
  pitsQuery: computed
});

export default BasicSearchStore;



// WEBPACK FOOTER //
// ./src/stores/BasicSearchStore.js